import React, { useEffect, useState } from 'react';
import { PayrollPeriod, PayrollPeriodStatus } from '../types';
import { getPayrollPeriods, createPayrollPeriod, updatePayrollPeriodStatus } from '../services/payrollPeriodService';
import { getSettings } from '../services/settingsService';
import { Calendar, Lock, Unlock, Sparkles, AlertTriangle, Clock, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';

interface Props {
  tenantId: string;
}

export const PayrollPeriodManager: React.FC<Props> = ({ tenantId }) => {
  const { user, role } = useFirebase();
  const isAdminOrManager = role?.toLowerCase() === 'admin' || role?.toLowerCase() === 'manager' || user?.email?.toLowerCase() === 'admin@growezy.ai';
  const [periods, setPeriods] = useState<PayrollPeriod[]>([]);
  const [newPeriod, setNewPeriod] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<any>(null);
  const [showManual, setShowManual] = useState(false);
  const [creationError, setCreationError] = useState('');
  const [creationSuccess, setCreationSuccess] = useState('');

  useEffect(() => {
    fetchPeriods();
    getSettings(tenantId).then(setSettings);
  }, [tenantId]);

  const fetchPeriods = async () => {
    setLoading(true);
    const data = await getPayrollPeriods(tenantId);
    
    // Auto-lock logic
    const now = new Date();
    for (const period of data) {
        if (period.status === 'Open' && period.dueDate) {
            const dueDate = new Date(period.dueDate);
            dueDate.setHours(dueDate.getHours() + 24); // Add 24 hours
            if (now > dueDate) {
                await updatePayrollPeriodStatus(tenantId, period.id!, 'Locked', 'System (Auto-Lock)');
                period.status = 'Locked';
            }
        }
    }
    
    setPeriods(data);
    setLoading(false);
  };

  // Compute dynamic suggestions
  const getSuggestedPeriod = () => {
    let suggestedStart = '';
    let suggestedEnd = '';
    let isFromAnchorOrPrior = false;
    let description = '';

    const cycleDays = settings?.payCycle === 'Bi-Weekly' || settings?.payCycle === 'Biweekly' ? 14 : 7;

    if (periods.length > 0) {
      // 1. Suggest immediately following the latest period's end date
      try {
        const lastPeriod = periods[0]; // sorted desc by startDate
        const lastEndDate = new Date(lastPeriod.endDate + 'T12:00:00');
        const nextStart = new Date(lastEndDate);
        nextStart.setDate(nextStart.getDate() + 1);
        
        const nextEnd = new Date(nextStart);
        nextEnd.setDate(nextEnd.getDate() + cycleDays - 1);

        suggestedStart = nextStart.toISOString().split('T')[0];
        suggestedEnd = nextEnd.toISOString().split('T')[0];
        isFromAnchorOrPrior = true;
        description = `Starts immediately after prior period ending on ${lastPeriod.endDate}`;
      } catch (e) {
        console.error('Error calculating suggestions from prior periods:', e);
      }
    } else if (settings?.anchorDate) {
      // 2. Suggest starting from the company's designated anchor date
      try {
        const anchor = new Date(settings.anchorDate + 'T12:00:00');
        const nextEnd = new Date(anchor);
        nextEnd.setDate(nextEnd.getDate() + cycleDays - 1);

        suggestedStart = settings.anchorDate;
        suggestedEnd = nextEnd.toISOString().split('T')[0];
        isFromAnchorOrPrior = true;
        description = `Calculated from your company's Payroll Anchor Date (${settings.anchorDate})`;
      } catch (e) {
        console.error('Error calculating suggestions from anchor date:', e);
      }
    } else {
      // 3. Fallback to today
      try {
        const todayStr = new Date().toISOString().split('T')[0];
        const today = new Date(todayStr + 'T12:00:00');
        const nextEnd = new Date(today);
        nextEnd.setDate(nextEnd.getDate() + cycleDays - 1);

        suggestedStart = todayStr;
        suggestedEnd = nextEnd.toISOString().split('T')[0];
        description = 'Defaulting to starting today (No Anchor Date set in Settings)';
      } catch (e) {
        console.error('Error calculating suggestions fallback:', e);
      }
    }

    return { suggestedStart, suggestedEnd, isFromAnchorOrPrior, description };
  };

  const { suggestedStart, suggestedEnd, isFromAnchorOrPrior, description } = getSuggestedPeriod();

  const handleCreatePeriod = async (startDate: string, endDate: string) => {
    setCreationError('');
    setCreationSuccess('');
    
    if (!isAdminOrManager) {
      setCreationError("Only Admins or Managers can create payroll periods.");
      return;
    }
    if (!startDate || !endDate) {
      setCreationError("Please provide both start and end dates.");
      return;
    }

    // Check for gaps or overlap if there are existing periods
    if (periods.length > 0) {
      const latestPeriod = periods[0];
      if (startDate <= latestPeriod.endDate) {
        setCreationError(`Overlap warning: The selected start date (${startDate}) is before or on the latest period's end date (${latestPeriod.endDate}).`);
        return;
      }
    }

    try {
      const payDayDueStr = settings?.payDayDue || 'Friday';
      const endDateObj = new Date(endDate + 'T12:00:00');
      const dayOfWeek = endDateObj.getDay();
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const targetDayIndex = days.indexOf(payDayDueStr);
      
      let diff = targetDayIndex - dayOfWeek;
      if (diff <= 0) diff += 7; // Ensure it refers to the upcoming date
      
      const dueDate = new Date(endDateObj);
      dueDate.setDate(dueDate.getDate() + diff);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      await createPayrollPeriod(tenantId, { 
        startDate, 
        endDate, 
        dueDate: dueDateStr, 
        status: 'Open' 
      });

      setCreationSuccess(`Successfully created pay period: ${startDate} to ${endDate}`);
      setNewPeriod({ startDate: '', endDate: '' });
      fetchPeriods();
      
      setTimeout(() => setCreationSuccess(''), 4000);
    } catch (err: any) {
      console.error(err);
      setCreationError('An error occurred while creating the pay period.');
    }
  };

  const toggleStatus = async (period: PayrollPeriod) => {
    if (!isAdminOrManager) {
      alert("Only Admins or Managers can lock/unlock payroll periods.");
      return;
    }
    if (!user?.email) return;
    const nextStatus: PayrollPeriodStatus = period.status === 'Open' ? 'Locked' : 'Open';
    await updatePayrollPeriodStatus(tenantId, period.id!, nextStatus, user.email);
    fetchPeriods();
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Dynamic Next Period Generator Card */}
      {isAdminOrManager && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-[10px] font-black uppercase tracking-wider">
                  <Sparkles size={11} className="text-blue-600 animate-pulse" />
                  Suggested Pay Period
                </span>
                <h3 className="text-sm font-black text-gray-900 mt-2">Next Scheduled Pay Cycle</h3>
                <p className="text-xs text-gray-500 font-medium leading-relaxed">{description}</p>
              </div>
              
              {!settings?.anchorDate && periods.length === 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 text-amber-700 rounded-lg text-[11px] font-bold border border-amber-200">
                  <AlertTriangle size={14} className="text-amber-500" />
                  No Anchor Date configured
                </div>
              )}
            </div>

            {/* Dates display */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex items-center gap-4 bg-white/80 backdrop-blur p-4 rounded-xl border border-gray-100 shadow-sm">
                <Calendar className="text-blue-500 w-5 h-5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase font-bold tracking-widest text-gray-400">Scheduled Dates</p>
                  <p className="text-sm font-extrabold text-gray-900 mt-0.5">{suggestedStart} to {suggestedEnd}</p>
                </div>
              </div>

              <button 
                onClick={() => handleCreatePeriod(suggestedStart, suggestedEnd)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-xl text-sm font-bold shadow-md shadow-blue-500/10 transition-all flex items-center justify-center gap-2"
              >
                <Sparkles size={16} />
                Generate This Period
              </button>
            </div>

            {/* Manual Toggle */}
            <div className="mt-5 pt-4 border-t border-blue-100 flex justify-between items-center text-xs">
              <span className="text-gray-400 italic font-medium">Standard cycle: {settings?.payCycle || 'Weekly'}</span>
              <button 
                onClick={() => setShowManual(!showManual)}
                className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-1 transition-colors"
              >
                {showManual ? 'Hide manual options' : 'Customize dates manually (Edge cases)'}
                {showManual ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>

          {/* Expandable Manual Date Customizer */}
          {showManual && (
            <div className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4 animate-in slide-in-from-top-3 duration-300">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" />
                <h4 className="text-xs font-black text-gray-700 uppercase tracking-widest">Customize Dates (Manual Override)</h4>
              </div>
              <p className="text-xs text-gray-500">Only use this to create irregular transition periods. Manual entry can create unintended gaps or overlaps in calculations.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input 
                    type="date" 
                    value={newPeriod.startDate} 
                    onChange={e => setNewPeriod({...newPeriod, startDate: e.target.value})} 
                    className="w-full p-3 border rounded-lg text-sm bg-white" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">End Date</label>
                  <input 
                    type="date" 
                    value={newPeriod.endDate} 
                    onChange={e => setNewPeriod({...newPeriod, endDate: e.target.value})} 
                    className="w-full p-3 border rounded-lg text-sm bg-white" 
                  />
                </div>
                <button 
                  onClick={() => handleCreatePeriod(newPeriod.startDate, newPeriod.endDate)} 
                  className="md:col-span-2 bg-gray-800 hover:bg-gray-900 text-white px-4 py-3 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  Create Custom Period
                </button>
              </div>
            </div>
          )}

          {/* Banner logs */}
          {creationError && (
            <div className="p-4 bg-rose-50 text-rose-800 rounded-xl text-xs font-medium border border-rose-100 flex items-center gap-2 animate-in slide-in-from-top-2">
              <AlertTriangle size={14} className="text-rose-500 shrink-0" />
              <span>{creationError}</span>
            </div>
          )}

          {creationSuccess && (
            <div className="p-4 bg-emerald-50 text-emerald-800 rounded-xl text-xs font-medium border border-emerald-100 flex items-center gap-2 animate-in slide-in-from-top-2">
              <CheckCircle size={14} className="text-emerald-500 shrink-0" />
              <span>{creationSuccess}</span>
            </div>
          )}
        </div>
      )}

      {/* Existing Periods List */}
      <div className="space-y-3">
        <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
          <Clock size={14} />
          Current & Past Pay Periods
        </h3>
        
        {periods.map(period => (
          <div key={period.id} className="p-5 border border-gray-100 rounded-xl flex items-center justify-between bg-white shadow-sm hover:border-gray-200 transition-colors">
            <div>
              <p className="text-sm font-black text-gray-900">{period.startDate} to {period.endDate}</p>
              <p className="text-xs text-gray-500">Due: {period.dueDate}</p>
              {period.status === 'Open' && <p className="text-[10px] text-amber-600 font-medium mt-1">Payroll is open. Lock when ready to finalize.</p>}
              {period.updatedBy && <p className="text-[10px] text-gray-400 font-medium">Last updated by: {period.updatedBy}</p>}
            </div>
            <div className="flex gap-4 items-center">
              <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${period.status === 'Locked' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                {period.status}
              </span>
              {period.status !== 'Processed' && isAdminOrManager && (
                <button
                  onClick={() => toggleStatus(period)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    period.status === 'Open'
                      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                  }`}
                >
                  {period.status === 'Open' ? <Lock size={14} /> : <Unlock size={14} />}
                  {period.status === 'Open' ? 'Lock Payroll' : 'Unlock Payroll'}
                </button>
              )}
            </div>
          </div>
        ))}
        {periods.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-6">No payroll periods defined.</p>}
      </div>
    </div>
  );
};
